var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { useCallback, useEffect, useMemo } from "react";
import tmi, {} from "tmi.js";
import { typeSafeObjectEntries } from "../utils/helpers";
import { useAmbassadors } from "./useAmbassadors";
import useChannel from "./useChannel";
var parseCsvEnv = function (env) { var _a; return (_a = env === null || env === void 0 ? void 0 : env.split(",").map(function (str) { return str.toLowerCase(); })) !== null && _a !== void 0 ? _a : []; };
var testChannelNames = parseCsvEnv(process.env.REACT_APP_TEST_CHANNEL_NAMES);
var defaultChannelNames = parseCsvEnv(process.env.REACT_APP_DEFAULT_CHANNEL_NAMES);
var extraChannelNames = parseCsvEnv(process.env.REACT_APP_EXTRA_CHANNEL_NAMES);
var privilegedUsers = parseCsvEnv(process.env.REACT_APP_CHAT_COMMANDS_PRIVILEGED_USERS);
export default function useChatCommand(callback) {
    var channel = useChannel();
    var channelNames = useMemo(function () {
        return Array.from(new Set(__spreadArray(__spreadArray(__spreadArray([], testChannelNames, true), (channel ? [channel] : defaultChannelNames), true), (channel && !defaultChannelNames.includes(channel)
            ? extraChannelNames
            : []), true).map(function (name) { return name.toLowerCase(); })));
    }, [channel]);
    var ambassadors = useAmbassadors();
    var commandsMap = useMemo(function () {
        var commands = new Map();
        if (ambassadors) {
            typeSafeObjectEntries(ambassadors).forEach(function (_a) {
                var key = _a[0], ambassador = _a[1];
                ambassador.commands.forEach(function (command) {
                    commands.set(command.toLowerCase(), key);
                });
            });
        }
        commands.set("welcome", "welcome");
        commands.set("refresh", "refresh");
        return commands;
    }, [ambassadors]);
    var messageHandler = useCallback(function (id, channel, tags, msg, self) {
        var _a, _b, _c, _d, _e;
        // Ignore if user is not a moderator or broadcaster or test user
        if (!tags.mod &&
            !((_a = tags.badges) === null || _a === void 0 ? void 0 : _a.broadcaster) &&
            !privilegedUsers.includes((_c = (_b = tags.username) === null || _b === void 0 ? void 0 : _b.toLowerCase()) !== null && _c !== void 0 ? _c : ""))
            return;
        // Ignore echoed messages (messages sent by the bot) and messages that don't start with '!'
        if (self || !msg.startsWith("!"))
            return;
        var _f = msg
            .slice(1)
            .trim()
            .replace(/\s+/g, " ")
            .split(" "), commandName = _f[0], args = _f.slice(1);
        var command = commandName && commandsMap.get(commandName.toLowerCase());
        if (!command)
            return;
        // Handle any special commands + privilege edge cases
        if (command === "refresh" &&
            !privilegedUsers.includes((_e = (_d = tags.username) === null || _d === void 0 ? void 0 : _d.toLowerCase()) !== null && _e !== void 0 ? _e : ""))
            return;
        console.log("*Twitch extension received command: ".concat(command, " (").concat(args, ")*"), id);
        callback(command, args);
    }, [commandsMap, callback]);
    useEffect(function () {
        var id = Date.now();
        console.log("*Twitch extension is connecting to chat*", id);
        // Create the client
        var client = new tmi.Client({
            connection: {
                secure: true,
                reconnect: true,
            },
            channels: channelNames.map(function (name) { return "#".concat(name); }),
        });
        // Handle incoming messages
        client.on("message", function () {
            var args = [];
            for (var _i = 0; _i < arguments.length; _i++) {
                args[_i] = arguments[_i];
            }
            return messageHandler.apply(void 0, __spreadArray([id], args, false));
        });
        // Handle race condition where we connect after being unmounted
        var closing = false;
        client.on("connected", function () {
            // If we connected after being unmounted, disconnect (again)
            if (closing) {
                client
                    .disconnect()
                    .then(function () {
                    return console.log("*Twitch extension disconnected from chat (after connecting)*", id);
                });
                return;
            }
            console.log("*Twitch extension is connected to chat: ".concat(channelNames.join(", "), "*"), id);
        });
        // Connect to chat
        client.connect();
        // Disconnect from chat when unmounting
        return function () {
            closing = true;
            client
                .disconnect()
                .then(function () {
                return console.log("*Twitch extension disconnected from chat*", id);
            });
        };
    }, [channelNames, messageHandler]);
}
